/**
 * Модель референса, выведенная из заметки, и группировка фото по моделям.
 *
 * Отдельного поля в схеме нет (`reference_item` хранит только заметку), а
 * заметки библиотеки платьев устроены как `Название · Коллекция · ссылка`.
 * Поэтому модель считаем на клиенте. Здесь же — что выделено и что удаляется на
 * уровне папок (`folderLevelShown`, `outsideLoose`, `deleteIds`). Модуль без
 * импортов: его напрямую грузит `node --test`.
 */
export type ModelInfo = { key: string; name: string; collection: string | null };
export type ModelFolder<T> = ModelInfo & { items: T[] };

const isLink = (s: string) => /^https?:\/\//.test(s);

/** Последний непустой сегмент пути ссылки в нижнем регистре; не разобралась — null. */
function linkKey(url: string): string | null {
  try {
    const seg = new URL(url).pathname.split("/").filter(Boolean).pop();
    return seg ? seg.toLowerCase() : null;
  } catch {
    return null;
  }
}

export function modelOf(note: string | null | undefined): ModelInfo | null {
  if (!note) return null;
  const parts = note.split(/\s*·\s*/).map((p) => p.trim());
  const link = /https?:\/\/\S+/.exec(note)?.[0];
  let key = link ? linkKey(link) : null;
  if (!key) {
    // без ссылки модель есть только при разделителе и непустой первой части
    const first = parts[0] ?? "";
    if (parts.length < 2 || !first || isLink(first)) return null;
    key = first.toLowerCase().replace(/\s+/g, "-");
  }
  const first = parts[0] ?? "";
  const second = parts[1] ?? "";
  return {
    key,
    name: first && !isLink(first) ? first : key[0]!.toUpperCase() + key.slice(1),
    collection: second && !isLink(second) ? second : null,
  };
}

/** Папки — в порядке первого фото модели; фото внутри и `loose` — в исходном порядке. */
export function groupByModel<T extends { note: string | null }>(
  items: readonly T[],
): { folders: ModelFolder<T>[]; loose: T[] } {
  const byKey = new Map<string, ModelFolder<T>>();
  const loose: T[] = [];
  for (const item of items) {
    const m = modelOf(item.note);
    if (!m) {
      loose.push(item);
      continue;
    }
    const folder = byKey.get(m.key);
    if (folder) folder.items.push(item);
    else byKey.set(m.key, { ...m, items: [item] });
  }
  return { folders: [...byKey.values()], loose };
}

/**
 * Что выделяется на уровне папок, в порядке экрана: обложки (первое фото) непустых
 * папок в порядке плиток, потом россыпь. По этому списку работают индексы, Shift-диапазон
 * и «все видимые»; пустая папка обложки не имеет и в него не входит.
 */
export function folderLevelShown<T>(folders: ModelFolder<T>[], loose: readonly T[]): T[] {
  const covers: T[] = [];
  for (const f of folders) if (f.items[0] !== undefined) covers.push(f.items[0]);
  return [...covers, ...loose];
}

/**
 * Выделено ли на уровне папок что-то, кроме видимой россыпи (обложки папок, в том числе
 * бывшие: срез сменился, и обложкой стало другое фото). `folders` не задан — не уровень папок.
 */
export function outsideLoose(
  selected: ReadonlySet<number>,
  folders: readonly unknown[] | undefined,
  loose: readonly { id: number }[],
): boolean {
  if (!folders) return false;
  const looseIds = new Set(loose.map((i) => i.id));
  return [...selected].some((id) => !looseIds.has(id));
}

/** Папки, чья обложка (первое фото в срезе) выделена; `folders` не задан — не уровень папок. */
export function selectedFolders<T extends { id: number }>(
  selected: ReadonlySet<number>,
  folders: readonly ModelFolder<T>[] | undefined,
): ModelFolder<T>[] {
  return (folders ?? []).filter((f) => f.items[0] !== undefined && selected.has(f.items[0].id));
}

/**
 * Что удалить по выделению, в порядке данных: выделенная обложка папки — ВСЕ фото этой
 * модели среди `items` (мимо фильтров: убираем модель целиком), прочие выделенные — как есть.
 * Бывшая обложка (срез сменился) и фото, выделенное внутри папки, не раскрываются.
 */
export function deleteIds(
  selected: ReadonlySet<number>,
  folders: readonly ModelFolder<{ id: number }>[] | undefined,
  items: readonly { id: number; note: string | null }[],
): number[] {
  const keys = new Set(selectedFolders(selected, folders).map((f) => f.key));
  const out = new Set<number>();
  for (const i of items) {
    const key = keys.size ? modelOf(i.note)?.key : undefined;
    if (selected.has(i.id) || (key !== undefined && keys.has(key))) out.add(i.id);
  }
  return [...out];
}
